import type { ColumnFilterEntry } from "./claims";

export interface UsageByAgent {
  classification: string;
  billable_count: number;
  cases: number;
  executions: number;
  pct_successful: number;
  total_cost: number;
}

export interface UsageKpis {
  billable_count: number;
  // billable_cases is the REV-1762 name; billable_count kept for back-compat.
  billable_cases: number;
  cases: number;
  completed: number;
  failed: number;
  fixed_cost: number;
  contingency_cost: number;
  total_cost: number;
  total_runs: number;
}

export interface CasesByUser {
  user_id: number | null;
  user_displayName: string;
  cases: number;
}

export interface CasesByPayer {
  payer: string;
  cases: number;
}

export interface CasesBreakdown {
  by_user: CasesByUser[];
  by_payer: CasesByPayer[];
}

export interface ObservabilityMainFilters {
  dateInterval: string;
  /** Inclusive start date (YYYY-MM-DD) sent to observability APIs. */
  customDateFrom: string;
  /** Inclusive end date (YYYY-MM-DD) sent to observability APIs. */
  customDateTo: string;
}

export const DEFAULT_OBSERVABILITY_MAIN_FILTERS: ObservabilityMainFilters = {
  dateInterval: "this_month",
  customDateFrom: "",
  customDateTo: "",
};

export interface ClaimOutcomeStatusCount {
  count: number;
  status: string;
}

export interface ClaimOutcomePayerCount {
  count: number;
  payer: string;
  status: string;
}

export interface ClaimOutcomeTracking {
  by_payer: ClaimOutcomePayerCount[];
  by_status: ClaimOutcomeStatusCount[];
}

export type UsageActivityFeedColumnFilters = Record<
  string,
  ColumnFilterEntry | string[]
>;

export interface UsageActivityFeedTotals {
  cost: number;
  contingency_cost: number;
  recovered_amount: number;
}

export interface UsageActivityFeedResponse {
  rows: UsageActivityFeed[];
  total: number;
  offset: number;
  limit: number;
  totals: UsageActivityFeedTotals | null;
}

export interface UsageActivityFeed {
  classification: string;
  assigned_to: string;
  chat_id: string;
  claim_number: string;
  finished_at: string;
  id: string;
  started_at: string;
  status: string;
  successful_run: boolean;
  user_id: number | null;
  user_displayName: string;
  has_837: boolean;
  count_835: number;
  count_document_uploads: number;
  cost: number;
  contingency_cost: number;
  recovered_amount: number;
  payer: string;
  billed_amount: number;
  paid_amount: number;
}

// One row per claim with a billable appeal (REV-1824): the claim's single
// recovery chain — success fee computed once per claim on the canonical
// appeal, no matter how many appeal chats the claim accumulated.
export interface SuccessFeePerClaimRow {
  claim_number: string;
  /** null for an appeal recorded on a claim with no letter: no chat behind it (REV-2541). */
  chat_id: string | null;
  payer: string;
  // TODO(backend): not yet returned by the success-fees-per-claim endpoint —
  // assumed to follow the same user_id/user_displayName pair used elsewhere
  // (e.g. UsageActivityFeed) once the backend adds it.
  user_id: number | null;
  user_displayName: string;
  appeal_date: string | null;
  // TODO(backend): not yet returned by the success-fees-per-claim endpoint —
  // the date the success fee was actually paid, distinct from appeal_date.
  payment_date: string | null;
  paid_at_appeal: number;
  paid_now: number;
  recovery_pct: number;
  recorded_fee: number;
  projected_fee: number;
  success_fee: number;
}

export interface SuccessFeesPerClaimTotals {
  recorded_fee: number;
  projected_fee: number;
  success_fee: number;
}

export interface SuccessFeesPerClaimResponse {
  rows: SuccessFeePerClaimRow[];
  totals: SuccessFeesPerClaimTotals;
}

export interface FinancialImpact {
  denied_claims: number;
  denied_dollars: number;
  claims_appealed: number;
  denied_to_paid_conversions: number;
  recovered_revenue: number;
  roi: number | null;
  total_cost: number;
}

export interface BillingSummary {
  total_cost: number;
  total_runs: number;
}

export interface DrillDownCapability {
  totals: {
    billable_bills: number;
    bills: number;
    flat_fee: number;
    grand_total: number;
    projected_recovery: number;
    recorded_recovery: number;
  };
  window: {
    from: string;
    is_current_open_cycle: boolean;
    to: string;
  };
}

export interface DrillDownCapabilityLineItemRow {
  classification: string;
  bill_id: string;
  /** "recorded_appeal" for an appeal recorded on a claim with no letter (REV-2541). */
  billable_reason: string;
  billing_month: string;
  /** null for those recorded-appeal bills: no chat behind them. */
  chat_id: string | null;
  claim_number: string;
  flat_fee: number;
  is_billable: boolean;
  priced_at: number;
  projected_recovery: number;
  recorded_recovery: number;
  simulated: boolean;
  started_at: string;
  total: number;
  user_id: number | null;
  user_displayName: string;
}

export interface DrillDownCapabilityLineItem {
  rows: DrillDownCapabilityLineItemRow[];
  window: {
    from: string;
    is_current_open_cycle: boolean;
    to: string;
  };
}

//#region Claim Status
export enum ObservabilityClaimStatus {
  RUNNING = "running",
  COMPLETED = "completed",
  FAILED = "failed",
  TIMEOUT = "timeout",
}
//#endregion
